import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  LinearProgress,
  Alert,
  Paper,
  Chip,
  useTheme,
  alpha,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Upload as UploadIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Description as TextIcon,
  CheckCircle as SuccessIcon,
  CloudUpload as CloudIcon,
} from '@mui/icons-material';
import { uploadScript, uploadNewVersion } from '../services/scriptService';

const ScriptUploader = ({ open, onClose, onUploadComplete, projectId, existingScript = null }) => {
  const theme = useTheme();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [revisionColor, setRevisionColor] = useState('White');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [displayFileName, setDisplayFileName] = useState('');
  const [originalFdxName, setOriginalFdxName] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const isNewScript = !existingScript;

  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return <PdfIcon sx={{ fontSize: 40, color: theme.palette.error.main }} />;
      case 'fdx':
      case 'script':
        return <FileIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />;
      case 'fountain':
      case 'txt':
        return <TextIcon sx={{ fontSize: 40, color: theme.palette.info.main }} />;
      case 'docx':
      case 'doc':
        return <FileIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />;
      default:
        return <FileIcon sx={{ fontSize: 40, color: theme.palette.text.secondary }} />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    if (!open) {
      // Reset form when dialog closes
      setFile(null);
      setTitle('');
      setNotes('');
      setRevisionColor('White');
      setError(null);
      setUploading(false);
      setDisplayFileName('');
      setOriginalFdxName(null);
    }
  }, [open]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  }, []);

  const validateAndSetFile = (selectedFile) => {
    const validTypes = ['.pdf', '.fountain', '.txt', '.script', '.docx', '.doc', '.fdx'];
    const fileExt = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    
    if (!validTypes.includes(fileExt)) {
      setError(`Invalid file type. Supported formats: ${validTypes.join(', ')}`);
      setFile(null);
      setDisplayFileName('');
      setOriginalFdxName(null);
      return;
    }

    const maxSize = 50 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setError('File size must be less than 50MB');
      setFile(null);
      return;
    }

    let fileToUpload = selectedFile;
    if (fileExt === '.fdx') {
      try {
        const renamedName = `${selectedFile.name.replace(/\.fdx$/i, '')}.script`;
        fileToUpload = new File([selectedFile], renamedName, {
          type: selectedFile.type || 'text/xml'
        });
        setOriginalFdxName(selectedFile.name);
      } catch (renameError) {
        setError('Could not process the Final Draft file for upload.');
        setFile(null);
        setDisplayFileName('');
        setOriginalFdxName(null);
        return;
      }
    } else {
      setOriginalFdxName(null);
    }

    setFile(fileToUpload);
    setDisplayFileName(selectedFile.name);
    setError(null);

    if (isNewScript && !title) {
      const defaultTitle = selectedFile.name.split('.')[0];
      setTitle(defaultTitle);
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    } else {
      setFile(null);
      setDisplayFileName('');
      setOriginalFdxName(null);
    }
  };

  const handleUpload = async () => {
    
    if (!file) {
      setError('Please select a file');
      return;
    }

    if (isNewScript && !title.trim()) {
      setError('Please enter a title');
      return;
    }

    if (!projectId) {
      setError('No project selected');
      return;
    }
    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('scriptFile', file);
    if (originalFdxName) {
      formData.append('originalFileName', originalFdxName);
      formData.append('originalExtension', '.fdx');
    }
    
    if (isNewScript) {
      formData.append('title', title.trim());
      formData.append('projectId', projectId);
      if (notes.trim()) {
        formData.append('notes', notes.trim());
      }
      formData.append('revisionColor', revisionColor);
    } else {
      if (notes.trim()) {
        formData.append('notes', notes.trim());
      }
      formData.append('revisionColor', revisionColor);
    }

    try {
      let response;
      if (isNewScript) {
        response = await uploadScript(formData);
      } else {
        response = await uploadNewVersion(existingScript._id, formData);
      }
      onUploadComplete(response);
      onClose();
    } catch (err) {
      console.error('Upload failed:', err.message);
      setError(err.message || 'Error uploading script');
    } finally {
      setUploading(false);
    }
  };

  const REVISION_COLORS = [
    { value: 'White', label: 'White (Original)', color: '#ffffff' },
    { value: 'Blue', label: 'Blue', color: '#2196f3' },
    { value: 'Pink', label: 'Pink', color: '#e91e63' },
    { value: 'Yellow', label: 'Yellow', color: '#ffeb3b' },
    { value: 'Green', label: 'Green', color: '#4caf50' },
    { value: 'Goldenrod', label: 'Goldenrod', color: '#daa520' },
  ];

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (uploading || reason === 'backdropClick') {
          return;
        }
        onClose();
      }} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={uploading}
      PaperProps={{
        sx: {
          borderRadius: 1,
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          pb: 1,
          pt: 2.5,
          px: 3
        }}
      >
        <Typography 
          variant="overline" 
          sx={{ 
            color: theme.palette.primary.main,
            fontWeight: 700,
            letterSpacing: 2,
            fontSize: '0.7rem',
            mb: 0.5,
            display: 'block'
          }}
        >
          {isNewScript ? 'NEW SCRIPT' : 'NEW VERSION'}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
          {isNewScript ? 'Upload Script' : 'Upload New Version'}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Box sx={{ pt: 1 }}>
          {isNewScript && (
            <TextField
              fullWidth
              label="Script Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              margin="normal"
              error={!!error && !title.trim()}
              helperText={error && !title.trim() ? 'Title is required' : ' '}
              disabled={uploading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                }
              }}
            />
          )}
          
          {/* Drag and Drop Zone */}
          <Paper
            elevation={0}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              mt: 2,
              mb: 2,
              p: 3,
              borderRadius: 1,
              border: `2px dashed ${
                isDragOver 
                  ? theme.palette.primary.main 
                  : alpha(theme.palette.divider, 0.3)
              }`,
              backgroundColor: isDragOver 
                ? alpha(theme.palette.primary.main, 0.04)
                : theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.3)
                  : alpha(theme.palette.background.paper, 0.5),
              transition: 'all 0.2s ease',
              cursor: uploading ? 'default' : 'pointer',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <input
              accept=".pdf,.fountain,.txt,.script,.docx,.doc,.fdx"
              style={{ display: 'none' }}
              id="script-file-upload"
              type="file"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <label htmlFor="script-file-upload" style={{ width: '100%', cursor: uploading ? 'default' : 'pointer' }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  textAlign: 'center',
                  py: file ? 2 : 4
                }}
              >
                {!file ? (
                  <>
                    <CloudIcon 
                      sx={{ 
                        fontSize: 48, 
                        color: isDragOver ? theme.palette.primary.main : theme.palette.primary.light,
                        mb: 2,
                        transition: 'all 0.2s ease',
                        transform: isDragOver ? 'scale(1.05)' : 'scale(1)'
                      }} 
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {isDragOver ? 'Drop your file here' : 'Drag & drop your script'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      or click to browse files
                    </Typography>
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      disabled={uploading}
                      sx={{
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3
                      }}
                    >
                      Select File
                    </Button>
                  </>
                ) : (
                  <Fade in>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      width: '100%',
                      justifyContent: 'center'
                    }}>
                      {getFileIcon(displayFileName)}
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {displayFileName}
                          {originalFdxName && (
                            <Chip 
                              label="Final Draft" 
                              size="small" 
                              color="success"
                              sx={{ ml: 1, height: 20 }}
                            />
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                      <SuccessIcon sx={{ color: 'success.main', ml: 1 }} />
                    </Box>
                  </Fade>
                )}
              </Box>
            </label>
          </Paper>

          {/* File Type Hints */}
          {!file && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, justifyContent: 'center', mb: 2 }}>
              {['.pdf', '.fdx', '.fountain', '.txt', '.docx'].map((ext) => (
                <Chip 
                  key={ext}
                  label={ext}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: 22,
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.default,
                    color: theme.palette.text.secondary,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                />
              ))}
            </Box>
          )}

          {/* Revision Color - Industry Standard */}
          <FormControl fullWidth margin="normal" disabled={uploading}>
            <InputLabel id="revision-color-label">Revision Color</InputLabel>
            <Select
              labelId="revision-color-label"
              value={revisionColor}
              label="Revision Color"
              onChange={(e) => setRevisionColor(e.target.value)}
              sx={{
                borderRadius: 1,
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }
              }}
            >
              {REVISION_COLORS.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: option.color,
                      border: option.value === 'White' ? '2px solid ' + theme.palette.divider : 'none',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                  />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            margin="normal"
            multiline
            rows={2}
            disabled={uploading}
            placeholder="Additional details about this revision..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              }
            }}
          />

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2, 
                borderRadius: 1
              }}
            >
              {error}
            </Alert>
          )}

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress 
                variant="indeterminate" 
                sx={{ 
                  borderRadius: 1,
                  height: 6,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                  }
                }} 
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                Uploading script...
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button 
          onClick={onClose} 
          disabled={uploading}
          sx={{ 
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!file || uploading || (isNewScript && !title.trim())}
          startIcon={<UploadIcon />}
          sx={{
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          {uploading ? 'Uploading...' : isNewScript ? 'Upload Script' : 'Upload Version'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScriptUploader;
